var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
class TransportHookRegistry {
  constructor() {
    /** @internal */
    __publicField(this, "_beforeSend", []);
    /** @internal */
    __publicField(this, "_onResponse", []);
  }
  /**
   * Register a callback that runs before each request is sent.
   * The callback may return a (possibly async) transformed `TransportRequest`.
   * Returns a cleanup function that removes the callback.
   */
  onBeforeSend(cb) {
    this._beforeSend.push(cb);
    return () => {
      const idx = this._beforeSend.indexOf(cb);
      if (idx !== -1) {
        this._beforeSend.splice(idx, 1);
      }
    };
  }
  /**
   * Register a callback that runs after each response is received.
   * Returns a cleanup function that removes the callback.
   */
  onResponse(cb) {
    this._onResponse.push(cb);
    return () => {
      const idx = this._onResponse.indexOf(cb);
      if (idx !== -1) {
        this._onResponse.splice(idx, 1);
      }
    };
  }
  /**
   * Wrap a `Transport` so all `onBeforeSend` and `onResponse` hooks are
   * invoked transparently on each call to `send()`.
   *
   * The wrapped transport always returns an `AsyncIterable<TransportResponse>`.
   * For queries and mutations (where the underlying transport returns a
   * `Promise`), the iterable emits exactly one value then completes.
   * For subscriptions and incremental delivery (where the underlying transport
   * returns an `AsyncIterable`), each chunk is forwarded in turn.
   *
   * Hooks run in registration order: all `onBeforeSend` callbacks complete
   * before the underlying `send()` is called; all `onResponse` callbacks fire
   * after each response value is received.
   */
  wrap(transport) {
    const {
      _beforeSend,
      _onResponse
    } = this;
    return {
      ...transport,
      send(request) {
        return {
          [Symbol.asyncIterator]() {
            let iter = null;
            let singlePromise = null;
            let done = false;
            return {
              async next() {
                if (done) {
                  return {
                    value: void 0,
                    done: true
                  };
                }
                if (iter === null && singlePromise === null) {
                  let req = request;
                  for (const cb of _beforeSend) {
                    req = await cb(req);
                  }
                  const result2 = transport.send(req);
                  if (result2 !== null && typeof result2 === "object" && Symbol.asyncIterator in result2) {
                    iter = result2[Symbol.asyncIterator]();
                  } else {
                    singlePromise = result2;
                  }
                }
                if (singlePromise !== null) {
                  const tr = await singlePromise;
                  for (const cb of _onResponse) {
                    cb(tr);
                  }
                  done = true;
                  return {
                    value: tr,
                    done: false
                  };
                }
                const result = await iter.next();
                if (result.done) {
                  done = true;
                  return {
                    value: void 0,
                    done: true
                  };
                }
                for (const cb of _onResponse) {
                  cb(result.value);
                }
                return result;
              },
              return(value) {
                var _a;
                done = true;
                return ((_a = iter == null ? void 0 : iter.return) == null ? void 0 : _a.call(iter, value)) ?? Promise.resolve({
                  value: void 0,
                  done: true
                });
              },
              throw(error) {
                var _a;
                done = true;
                return ((_a = iter == null ? void 0 : iter.throw) == null ? void 0 : _a.call(iter, error)) ?? Promise.reject(error);
              }
            };
          }
        };
      }
    };
  }
}
export {
  TransportHookRegistry
};
//# sourceMappingURL=transport-hooks.js.map
